import React, { useState, useEffect, memo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@/components/modal';
import { Button } from '@/components/button/Buttons';

// Role data type definition
type RoleData = {
  name: string;
  has_admin_panel_access: boolean;
  has_user_front_access: boolean;
  description: string;
};

interface AddRoleModalProps {
  isOpen: boolean;
  isCreating: boolean;
  onClose: () => void;
  onCreateRole: (roleData: RoleData) => void;
}

const initialRoleData: RoleData = {
  name: '',
  has_admin_panel_access: true,
  has_user_front_access: false,
  description: '',
};

const AddRoleModal: React.FC<AddRoleModalProps> = ({
  isOpen,
  isCreating,
  onClose,
  onCreateRole,
}) => {
  const [roleData, setRoleData] = useState<RoleData>(initialRoleData);
  const [formErrors, setFormErrors] = useState<{ name?: string }>({});

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRoleData(initialRoleData);
      setFormErrors({});
    }
  }, [isOpen]);

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setRoleData((prev) => ({ ...prev, [id]: value }));

    // Clear name error if user types in the field
    if (id === 'name' && formErrors.name) {
      setFormErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    const fieldName =
      id === 'adminPanelAccess'
        ? 'has_admin_panel_access'
        : 'has_user_front_access';
    setRoleData((prev) => ({ ...prev, [fieldName]: checked }));
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    const errors: { name?: string } = {};

    if (!roleData.name.trim()) {
      errors.name = 'Role name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (validateForm()) {
      onCreateRole(roleData);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalContent className="max-w-md mx-auto mt-20">
        <ModalHeader>
          <ModalTitle>Add New Role</ModalTitle>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Role Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={roleData.name}
                  onChange={handleInputChange}
                  className={`w-full input px-3 py-2 border ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Enter role name"
                  required
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="adminPanelAccess"
                    checked={roleData.has_admin_panel_access}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="adminPanelAccess"
                    className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Admin Panel Access
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="userFrontAccess"
                    checked={roleData.has_user_front_access}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 text-blue-600 cursor-pointer bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="userFrontAccess"
                    className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    User Front Access
                  </label>
                </div>
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  value={roleData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full textarea px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter role description"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isCreating || !roleData.name.trim()}
              >
                {isCreating ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Role'
                )}
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AddRoleModal);
